import { createRouter, createWebHashHistory } from 'vue-router'
import type {
  Router,
  RouteLocationNormalized,
  RouteRecordNormalized,
  RouteRecordRaw
} from 'vue-router'
import { isUrl } from '@/utils/is'
import { omit, cloneDeep } from 'lodash-es'

const modules = import.meta.glob('../views/**/*.{vue,tsx}')

/** 后端菜单数据格式 */
export interface ServerMenuInfo {
  id: string
  parentId: string
  name: string
  code: string
  path: string
  icon?: string
  component?: string
  sort: number
  children?: ServerMenuInfo[]
  /** 自定义 meta 数据，如 { dashboardId: 'xxx' } */
  meta?: Record<string, any>
}

/**
 * 从路径中分离查询参数
 * 如: /superset?dashboardId=abc-123 -> { cleanPath: '/superset', query: { dashboardId: 'abc-123' } }
 */
const parsePathQuery = (rawPath: string): { cleanPath: string; query: Record<string, string> } => {
  const qIdx = rawPath.indexOf('?')
  if (qIdx === -1) return { cleanPath: rawPath, query: {} }
  const cleanPath = rawPath.substring(0, qIdx)
  const query: Record<string, string> = {}
  rawPath
    .substring(qIdx + 1)
    .split('&')
    .forEach((pair) => {
      const eqIdx = pair.indexOf('=')
      if (eqIdx === -1) return
      query[decodeURIComponent(pair.substring(0, eqIdx))] = decodeURIComponent(
        pair.substring(eqIdx + 1)
      )
    })
  return { cleanPath, query }
}

/**
 * 将后端菜单路径转换为组件路径
 * 如: /system/menu/index -> views/System/Menu
 * 如: /system/user/index -> views/System/User
 */
const pathToComponentPath = (path: string): string => {
  // 移除开头的斜杠，移除末尾的 /index
  const cleanPath = path.replace(/^\//, '').replace(/\/index$/i, '')
  // 将路径转为 PascalCase: system/menu -> System/Menu
  const parts = cleanPath.split('/').filter(Boolean)
  const pascalParts = parts.map((s) => s.charAt(0).toUpperCase() + s.slice(1))
  return 'views/' + pascalParts.join('/')
}

const resolveViewModule = (component: string) => {
  // 后端菜单配置可能带有首尾或路径分段空格，例如 " /Activity/Report/index"。
  // 清洗后统一以相对 views 目录的路径匹配组件。
  const normalized = component
    .trim()
    .replace(/^\/+/, '')
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean)
    .join('/')
  const withViewsPrefix = normalized.startsWith('views/') ? normalized : `views/${normalized}`
  const pascalComponent = normalized.startsWith('views/')
    ? normalized
    : pathToComponentPath(normalized)

  return (
    modules[`../${normalized}.vue`] ||
    modules[`../${normalized}.tsx`] ||
    modules[`../${normalized}/index.vue`] ||
    modules[`../${normalized}/index.tsx`] ||
    modules[`../${withViewsPrefix}.vue`] ||
    modules[`../${withViewsPrefix}.tsx`] ||
    modules[`../${withViewsPrefix}/index.vue`] ||
    modules[`../${withViewsPrefix}/index.tsx`] ||
    modules[`../${pascalComponent}.vue`] ||
    modules[`../${pascalComponent}.tsx`] ||
    modules[`../${pascalComponent}/index.vue`] ||
    modules[`../${pascalComponent}/index.tsx`]
  )
}

/**
 * 规范化路径，确保格式一致
 * 如: system/menu/index -> /system/menu/index
 * 如: /system/user/index -> /system/user/index
 */
const normalizePath = (path: string): string => {
  if (!path) return ''
  return path.startsWith('/') ? path : '/' + path
}

/** 将后端菜单数据转换为路由格式 */
export const transformMenuToRoute = (
  menus: ServerMenuInfo[],
  parentPath = ''
): AppCustomRouteRecordRaw[] => {
  return menus.map((menu) => {
    const isTopLevel = !parentPath || menu.parentId === '0' || menu.parentId === ''
    const hasChildren = menu.children && menu.children.length > 0

    // 分离路径中的查询参数，如 /superset?dashboardId=abc -> cleanPath + query
    const { cleanPath: menuCleanPath, query: pathQuery } = parsePathQuery(menu.path)
    const hasQuery = Object.keys(pathQuery).length > 0

    // 规范化完整路径（不含查询参数）
    const fullPath = normalizePath(menuCleanPath)

    // 生成路由名称：将路径转为 PascalCase，如 /system/menu/index -> SystemMenu
    const routeName = fullPath
      .replace(/\/index$/i, '') // 移除末尾的 /index
      .split('/')
      .filter(Boolean)
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join('')

    // 生成组件路径
    const componentPath = isTopLevel ? '#' : pathToComponentPath(fullPath)

    // 计算子路由的相对路径
    // 如父路由 /system，子路由完整路径 /system/menu/index -> 相对路径 menu/index
    let routePath = fullPath
    if (!isTopLevel && parentPath) {
      const parentPrefix = parentPath.endsWith('/') ? parentPath : parentPath + '/'
      if (fullPath.startsWith(parentPrefix)) {
        routePath = fullPath.slice(parentPrefix.length)
      } else if (fullPath.startsWith(parentPath)) {
        routePath = fullPath.slice(parentPath.length + 1)
      }
    }

    // 子菜单 redirect 也需要去除查询参数
    let redirectPath = ''
    if (hasChildren) {
      const { cleanPath: childClean } = parsePathQuery(menu.children![0].path)
      redirectPath = normalizePath(childClean)
    }

    const route: AppCustomRouteRecordRaw = {
      path: isTopLevel ? fullPath : routePath,
      name: routeName || menu.code,
      component: menu.component || componentPath,
      redirect: redirectPath,
      meta: {
        title: menu.name,
        icon: menu.icon || (isTopLevel ? 'ep:menu' : undefined),
        // Keep parent menu visible even with a single child
        alwaysShow: hasChildren,
        // 保留原始带查询参数的路径，用于菜单导航
        ...(hasQuery ? { originalPath: normalizePath(menu.path) } : {}),
        // 合并后端传入的自定义 meta（如 dashboardId）
        ...(menu.meta || {})
      }
    }

    if (hasChildren) {
      route.children = transformMenuToRoute(menu.children!, fullPath)
    }

    return route
  })
}

/* Layout */
export const Layout = () => import('@/layout/Layout.vue')

export const getParentLayout = () => {
  return () =>
    new Promise((resolve) => {
      resolve({
        name: 'ParentLayout'
      })
    })
}

export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path
        }))
      : undefined) as RouteRecordNormalized[]
  }
}

// 前端控制路由生成
export const generateRoutesByFrontEnd = (
  routes: AppRouteRecordRaw[],
  keys: string[],
  basePath = '/'
): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const meta = route.meta ?? {}
    // skip some route
    if (meta.hidden && !meta.canTo) {
      continue
    }

    let data: Nullable<AppRouteRecordRaw> = null

    let onlyOneChild: Nullable<string> = null
    if (route.children && route.children.length === 1 && !meta.alwaysShow) {
      onlyOneChild = (
        isUrl(route.children[0].path)
          ? route.children[0].path
          : pathResolve(pathResolve(basePath, route.path), route.children[0].path)
      ) as string
    }

    // 开发者可以根据实际情况进行扩展
    for (const item of keys) {
      // 通过路径去匹配
      if (isUrl(item) && (onlyOneChild === item || route.path === item)) {
        data = Object.assign({}, route)
      } else {
        const routePath = (onlyOneChild ?? pathResolve(basePath, route.path)).trim()
        if (routePath === item || meta.followRoute === item) {
          data = Object.assign({}, route)
        }
      }
    }

    // recursive child routes
    if (route.children && data) {
      data.children = generateRoutesByFrontEnd(
        route.children,
        keys,
        pathResolve(basePath, data.path)
      )
    }
    if (data) {
      res.push(data as AppRouteRecordRaw)
    }
  }
  return res
}

// 后端控制路由生成
export const generateRoutesByServer = (routes: AppCustomRouteRecordRaw[]): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const data: AppRouteRecordRaw = {
      path: route.path,
      name: route.name,
      redirect: route.redirect,
      meta: route.meta
    }
    if (route.component) {
      const component = route.component as string
      if (component.includes('#')) {
        // 动态加载路由文件，可根据实际情况进行自定义逻辑
        data.component = component === '#' ? Layout : getParentLayout()
      } else {
        const comModule = resolveViewModule(component)
        if (!comModule) {
          console.error(`未找到${route.component}.vue文件或${route.component}.tsx文件，请创建`)
        } else {
          data.component = comModule
        }
      }
    }
    // recursive child routes
    if (route.children) {
      data.children = generateRoutesByServer(route.children)
    }
    res.push(data as AppRouteRecordRaw)
  }
  return res
}

export const pathResolve = (parentPath: string, path: string) => {
  if (isUrl(path)) return path
  if (path.startsWith('/')) return path
  const childPath = !path ? path : `/${path}`
  return `${parentPath}${childPath}`.replace(/\/\//g, '/').trim()
}

// 路由降级
export const flatMultiLevelRoutes = (routes: AppRouteRecordRaw[]) => {
  const modules: AppRouteRecordRaw[] = cloneDeep(routes)
  for (let index = 0; index < modules.length; index++) {
    const route = modules[index]
    if (!isMultipleRoute(route)) {
      continue
    }
    promoteRouteLevel(route)
  }
  return modules
}

// 层级是否大于2
const isMultipleRoute = (route: AppRouteRecordRaw) => {
  if (!route || !Reflect.has(route, 'children') || !route.children?.length) {
    return false
  }

  const children = route.children

  let flag = false
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    if (child.children?.length) {
      flag = true
      break
    }
  }
  return flag
}

// 生成二级路由
const promoteRouteLevel = (route: AppRouteRecordRaw) => {
  let router: Router | null = createRouter({
    routes: [route as RouteRecordRaw],
    history: createWebHashHistory()
  })

  const routes = router.getRoutes()
  addToChildren(routes, route.children || [], route)
  router = null

  route.children = route.children?.map((item) => omit(item, 'children'))
}

// 添加所有子菜单
const addToChildren = (
  routes: RouteRecordNormalized[],
  children: AppRouteRecordRaw[],
  routeModule: AppRouteRecordRaw
) => {
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    const route = routes.find((item) => item.name === child.name)
    if (!route) {
      continue
    }
    routeModule.children = routeModule.children || []
    if (!routeModule.children.find((item) => item.name === route.name)) {
      routeModule.children?.push(route as unknown as AppRouteRecordRaw)
    }
    if (child.children?.length) {
      addToChildren(routes, child.children, routeModule)
    }
  }
}
